import React, { memo, useMemo, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import Tooltip from "@8x8/oxygen-tooltip";
import List from "@8x8/oxygen-list";
import DotsVerticalIcon from "@8x8/oxygen-icon/icons/DotsVerticalIcon";
import Resizer from "@/react/components/table/components/Resizer";
import {
  StyledArrowDownFullIcon,
  StyledArrowUpFullIcon,
  StyledFilterIcon,
} from "@/react/components/styled/StyledIcons";
import ButtonDropdown from "@/react/components/ButtonDropdown";
import {
  ColumnSetterWrapper,
  CursorPointerListItem,
  HeaderContainer,
  MarginLeftSpan,
  SortButtonWrapper,
  TableCell,
} from "@/react/components/table/components/styled";

function TableHeadCell({
  headerRender,
  tooltip,
  canResize,
  size,
  resizeHandler,
  isResizing,
  canSort,
  canFilter,
  addBlankFilter,
  canEdit,
  column,
  rows,
  table,
}) {
  const [headerRef, setHeaderRef] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const tooltipRender = useMemo(
    () =>
      tooltip && (
        <Tooltip
          title={tooltip}
          includeWrapper={false}
          triggerRef={headerRef}
        />
      ),
    [headerRender, headerRef, tooltip]
  );

  const doAndClose = (callback) => (e) => {
    e.stopPropagation();
    callback();
    setIsDropdownOpen(false);
  };
  const isCustomTooltip =
    headerRender?.constructor === String && tooltip && headerRender !== tooltip;

  return (
    <TableCell size={size} isHead role="columnheader">
      <HeaderContainer
        canResize={canResize}
        isCustomTooltip={isCustomTooltip}
        ref={setHeaderRef}
      >
        {headerRender}
      </HeaderContainer>
      {!isResizing && canResize && headerRef && tooltipRender}
      {canSort && column.getIsSorted() && (
        <SortButtonWrapper onClick={column.getToggleSortingHandler()}>
          {column.getIsSorted() === "asc" ? (
            <StyledArrowUpFullIcon />
          ) : (
            <StyledArrowDownFullIcon />
          )}
        </SortButtonWrapper>
      )}
      {(canFilter || canEdit) && (
        <ButtonDropdown
          target={<DotsVerticalIcon size={18} />}
          isOpen={isDropdownOpen}
          setIsOpen={setIsDropdownOpen}
          dropdownKey={`Column-${column.columnDef.id}`}
        >
          <List isGroup>
            <CursorPointerListItem
              isDisabled={!canFilter}
              onClick={doAndClose(addBlankFilter)}
            >
              <StyledFilterIcon />
              <MarginLeftSpan>Filter</MarginLeftSpan>
            </CursorPointerListItem>
          </List>
          <List isGroup>
            <CursorPointerListItem
              onClick={doAndClose(() => column.toggleSorting(false))}
              isDisabled={!canSort}
            >
              <StyledArrowUpFullIcon />
              <MarginLeftSpan>Sort ascending</MarginLeftSpan>
            </CursorPointerListItem>
            <CursorPointerListItem
              onClick={doAndClose(() => column.toggleSorting(true))}
              isDisabled={!canSort}
            >
              <StyledArrowDownFullIcon />
              <MarginLeftSpan>Sort descending</MarginLeftSpan>
            </CursorPointerListItem>
          </List>
          {canEdit && column.columnDef.columnSetter && (
            <ColumnSetterWrapper>
              {flexRender(column.columnDef.columnSetter, {
                column,
                table,
                rows,
              })}
            </ColumnSetterWrapper>
          )}
        </ButtonDropdown>
      )}
      {canResize && (
        <Resizer resizeHandler={resizeHandler} isResizing={isResizing} />
      )}
    </TableCell>
  );
}

export default memo(TableHeadCell);
